import React from 'react';
import { DetailCardType } from 'types/DetailPageTypes';
import { TabType } from 'types/TabTypes';
import DetailPage from 'components/layout/DetailPage';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { HeaderButtons } from 'types/ButtonTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faEllipsis, faUser } from '@fortawesome/free-solid-svg-icons';
import LocationForm from './LocationForm';
import { buildAdminOnlyContactTypeOptions, useAdminOnlyContactTypes } from './contactTypeOptions';

// Rename the name of the function:
const PersonDetail: React.FC = () => {

    // Specify the parameters of this detail page:
    const apiObject = 'contact'
    const pageObject = 'person'
    const headerTitleDataField = 'name'
    const pageIcon = <FontAwesomeIcon icon={faUser} />

    // Show the organization of the person in front of the name, linking to its detail page
    const headerParent = {
        dataField: 'organization',
        labelField: 'name',
        urlPrefix: '/contacts/organization/',
        icon: <FontAwesomeIcon icon={faBuilding} />,
    }

    // Define the header buttons:
    const headerButtons: HeaderButtons = [
        {
            type: 'dropdown',
            icon: <FontAwesomeIcon icon={faEllipsis} />,
            tooltipText: 'button.more_button_label',
            size: 'medium',
            buttons: [
                {
                    label: 'button.merge_button_label',
                    action: 'merge',
                    allowedRight: ['can_delete'],
                },
                {
                    label: 'contact.convert.to_organization_label',
                    action: 'convert_to_organization',
                    allowedRight: ['can_delete'],
                },
                {
                    label: 'general.delete',
                    action: 'delete',
                    onSuccess: 'redirect',
                    redirectTo: '/contacts',
                    danger: true,
                },
            ],
        },
    ]

    // Only show the tickets tab when the tickets feature is active
    const ticketsActive = useActiveFeature('tickets');

    // The cancelled type is only offered inside the admin environment
    const adminOnlyTypeOptions = buildAdminOnlyContactTypeOptions(useAdminOnlyContactTypes(), 'person.contact_type');

    // Define the detail page tabs:
    const tabs: TabType[] = [ 
        { label: 'timeline.general.timeline_label', type: 'timeline', showOnlyTimelineUpdates: false },
        ...(ticketsActive ? [{ label: 'ticket.general.tickets_label', type: 'tickets' }] : []),
        { label: 'proposal.general.proposals_label', type: 'proposals' },
        { label: 'job.general.jobs_label', type: 'jobs' },
        { label: 'invoice.general.invoices_label', type: 'invoices' },
        { label: 'document.general.documents_label', type: 'attachments' },
    ]

    // Define detail components and add fields to FieldGroup cards:
    const detailCards: DetailCardType[] = [
        {
            component: 'FieldsCard',
            title: 'person.general.general_card_title',
            alignment: 'horizontal',
            fields: [
                { 
                    type: 'text', 
                    name: 'title', 
                    label: 'person.general.salutation_label', 
                    placeholder: 'person.general.salutation_placeholder', 
                },
                { 
                    type: 'text', 
                    name: 'first_name', 
                    label: 'person.general.first_name_label', 
                    placeholder: 'person.general.first_name_placeholder', 
                },
                { 
                    type: 'text', 
                    name: 'last_name', 
                    label: 'person.general.last_name_label', 
                    placeholder: 'person.general.last_name_placeholder', 
                },
                { 
                    type: 'text', 
                    name: 'role', 
                    label: 'person.general.role_label', 
                    placeholder: 'person.general.role_placeholder', 
                },
                {
                    type: 'custom-fields',
                    name: 'custom_fields',
                }
            ]
        },
        {
            component: 'PersonOrganizationCard',
        },
        {
            component: 'FieldsCard',
            title: 'person.general.detail_card_title',
            alignment: 'horizontal',
            fields: [
                { 
                    type: 'text', 
                    name: 'number', 
                    label: 'person.general.number_label', 
                    placeholder: 'person.general.number_placeholder', 
                },
                { 
                    type: 'text', 
                    name: 'reference', 
                    label: 'person.general.reference_label', 
                    placeholder: 'person.general.reference_placeholder', 
                    tooltipText: 'person.general.reference_tooltip',
                },
                { 
                    type: 'dropdown', 
                    name: 'contact_type', 
                    label: 'person.general.contact_type_label', 
                    disabled_selected: 'forms.select_type_disabled_selected',
                    defaultValue: { value: 'contact' },
                    allowNoneOption: false,
                    showSearch: false,
                    hardcodedOptions: [
                        { value: 'contact', name: 'person.contact_type.contact'},
                        { value: 'lead', name: 'person.contact_type.lead'},
                        { value: 'client', name: 'person.contact_type.client'},
                        ...adminOnlyTypeOptions,
                    ],
                    // A person of an organization is always a plain contact, so the type is hidden
                    dataCondition: [{ field: 'organization', values: [null] }],
                },
                {
                    type: 'switch',
                    name: 'blocked',
                    label: 'contact.general.blocked_label',
                    tooltipText: 'contact.general.blocked_tooltip',
                    onValue: 'forms.true_value',
                    offValue: 'forms.false_value',
                },
                { 
                    type: 'multiselect', 
                    name: 'labels', 
                    label: 'person.general.labels_label', 
                    apiObject: 'label',
                    apiRequest: [{
                        endpoint: 'get_label_list',
                        object: 'label',
                        params: { deleted: false, connected_feature: 'people' }
                    }],
                    allowNoneOption: false,
                    showSearch: false,
                    disabled_selected: 'person.general.select_label_disabled_selected',
                },
                { 
                    name: 'primary_locations',
                    type: 'locations', 
                    label: 'person.general.address_label',
                    apiField: 'primary_locations',
                    addNewItem: { 
                        modalForm: <LocationForm />, 
                        linkedObject: 'primary_locations' 
                    }
                },
                { 
                    name: 'phone_numbers',
                    type: 'phonenumbers',
                },
                { 
                    name: 'email_addresses',
                    type: 'emailaddresses',
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'contact.general.internal_note_label',
            alignment: 'stacked',
            fields: [
                {
                    name: 'internal_note',
                    type: 'textarea',
                    placeholder: 'contact.general.notes_placeholder',
                    isNote: true,
                },
            ]
        },
    ];

    return (
        <DetailPage
            apiObject={apiObject}
            pageObject={pageObject}
            pageIcon={pageIcon}
            headerParent={headerParent}
            headerTitleDataField={headerTitleDataField}
            headerButtons={headerButtons}
            detailCards={detailCards}
            tabs={tabs}
        />
    );
};

export default PersonDetail;